import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';

import { Route, FeatureFlag } from 'config';

import { isCreditCardExpired } from 'utils/dateTimes';
import { getQueryParam } from 'utils/location';
import { toCurrency } from 'utils/numbers';
import useCurrentUser from 'hooks/useCurrentUser';

import PaymentMethod from 'components/shared/PaymentMethod';
import PaymentModal from 'components/shared/modals/PaymentModal';
import SkeletonLine from 'components/shared/skeletons/SkeletonLine';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { removeTrailingDecimals } from '../../../utils/strings';

type Props = { className?: string; };

function PaymentInformationCard({ className }: Props): ReactElement {
  const { currentUser } = useCurrentUser();
  const {
    isEmpty, credit, payment_method: paymentMethod, coupon_message: couponMessage,
    coupon_tooltip_message: couponTooltipMessage,
    active_coupon: activeCoupon,
  } = currentUser;
  const hasCreditCardMethod = paymentMethod && paymentMethod.cc_brand;
  const editPaymentParam = getQueryParam('edit') === 'payment';
  const [showEditPayment, setShowEditPayment] = useState(editPaymentParam);
  const isGiftUser = !isEmpty && !paymentMethod;

  useEffect(() => {
    setShowEditPayment(editPaymentParam);
  }, [editPaymentParam]);

  const getExpiryDate = () => (
    `${paymentMethod.cc_exp_month}/${paymentMethod.cc_exp_year}`
  );

  const isExpired = hasCreditCardMethod && isCreditCardExpired(
    paymentMethod.cc_exp_month,
    paymentMethod.cc_exp_year,
  );

  const onClose = () => {
    setShowEditPayment(false);

    if (editPaymentParam) {
      window.history.pushState(null, '', Route.PROFILE);
    }
  };

  return (
    <div className={`c-card c-profile__card col-12 col-sm-6 col-lg-4 ${className}`}>
      <div className="c-card__content">
        <h2 className="c-profile__card-heading">
          Your Payment
          <button
            type="button"
            className="c-link u-text--sm"
            aria-label="Edit payment information"
            onClick={() => setShowEditPayment(true)}
          >
            Edit
          </button>
        </h2>

        {!isGiftUser && (
          <>
            <h3 className="c-profile__label">
              Your
              {hasCreditCardMethod ? ' Credit Card' : ' Payment Method'}
            </h3>

            {paymentMethod ? (
              <PaymentMethod
                paymentMethod={paymentMethod}
                iconClassName="c-profile__payment-icon"
              />
            ) : (
              <SkeletonLine width={120} height={14} />
            )}
          </>
        )}

        {isGiftUser && (
          <>
            <span>No credit card saved.</span>
            &nbsp;
            <button
              type="button"
              className="c-link u-text--sm"
              aria-label="Add payment information"
              onClick={() => setShowEditPayment(true)}
            >
              Add
            </button>
          </>
        )}

        {hasCreditCardMethod && (
          <>
            <h3 className="c-profile__label">Expiry Date</h3>

            <span className={isExpired ? 'c-profile__expired-cc' : ''}>
              {getExpiryDate()}
            </span>
          </>
        )}

        <h3 className="c-profile__label">Remaining Credits</h3>

        {isEmpty ? (
          <SkeletonLine width={120} height={14} />
        ) : (
          <span>{toCurrency(credit)}</span>
        )}

        <h3 className="c-profile__label">Applied Discounts</h3>

        {activeCoupon && (
        <span>
          {couponMessage}
          <OverlayTrigger
            placement="bottom"
            overlay={(
              <Tooltip id="applied-discounts-tooltip">
                {removeTrailingDecimals(couponTooltipMessage)}
              </Tooltip>
                  )}
          >
            <button
              className="c-link c-info-icon c-info-symbol"
              type="button"
              tabIndex={0}
            >
              &#9432;
            </button>
          </OverlayTrigger>
        </span>
        )}

        <PaymentModal
          show={showEditPayment}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

export default PaymentInformationCard;
